// AuthScreen (§10.1) — Sign In / Sign Up / Reset Password / Verify Email + Google.
// Reuses the existing firebaseService auth flows; only the UI is redesigned.
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Screen from "../ui/Screen";
import {
  signIn,
  signUp,
  signInWithGoogle,
  sendPasswordReset,
  resendVerificationEmail,
} from "../../services/firebaseService";

type AuthView = "signin" | "signup" | "reset" | "verify" | "reset-sent";

const friendlyError = (err: any): string => {
  const code = err?.code || "";
  if (err?.message === "EMAIL_NOT_VERIFIED") return "Please verify your email first — we just sent a new link.";
  switch (code) {
    case "auth/wrong-password": return "Incorrect password. Try again.";
    case "auth/user-not-found": return "No account found with that email.";
    case "auth/too-many-requests": return "Too many attempts. Please wait a moment and try again.";
    case "auth/network-request-failed": return "Network error. Check your connection.";
    case "auth/email-already-in-use": return "That email is already registered. Try signing in.";
    case "auth/invalid-email": return "That doesn't look like a valid email.";
    case "auth/weak-password": return "Password must be at least 6 characters.";
    default: return err?.message || "Something went wrong. Please try again.";
  }
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  height: 48,
  padding: "0 14px",
  borderRadius: 12,
  background: "var(--surface)",
  border: "1px solid var(--border)",
  color: "var(--text)",
  outline: "none",
  fontFamily: "'DM Sans',sans-serif",
  fontSize: 15,
};
const labelStyle: React.CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 };
const primaryBtn: React.CSSProperties = { minHeight: 50, border: "none", borderRadius: 14, background: "#65A30D", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 10px 24px rgba(101,163,13,0.4)" };

const AuthScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as any)?.returnTo || "/you";

  const [view, setView] = useState<AuthView>("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      if (view === "signup") {
        if (!name || !email || !pwd) throw new Error("Enter your name, email and password.");
        await signUp(email, pwd, name);
        setView("verify");
      } else if (view === "reset") {
        if (!email) throw new Error("Enter your email.");
        await sendPasswordReset(email);
        setView("reset-sent");
      } else if (view === "signin") {
        if (!email || !pwd) throw new Error("Enter your email and password.");
        await signIn(email, pwd);
        navigate(returnTo, { replace: true });
      }
    } catch (err: any) {
      if (err?.message === "EMAIL_NOT_VERIFIED") setView("verify");
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
      navigate(returnTo, { replace: true });
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, minHeight: 44, border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700,
    color: active ? "#fff" : "var(--text-2)", background: active ? "var(--accent)" : "transparent",
  });

  const logo = (
    <div style={{ textAlign: "center", margin: "6px 0 2px" }}>
      <div style={{ width: 56, height: 56, margin: "0 auto", borderRadius: 16, background: "radial-gradient(circle at 34% 28%, #BEF264, #65A30D 55%, #3F6212)", boxShadow: "0 8px 22px rgba(101,163,13,0.5)" }} />
    </div>
  );

  return (
    <Screen title="Account" showBack hideNav hideChips onBack={() => navigate(-1)}>
      <div className="mc-in" style={{ padding: "20px 16px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 460, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        {logo}

        {view === "reset-sent" && (
          <div style={{ textAlign: "center", padding: "26px 20px", borderRadius: 20, background: "var(--surface)", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 40 }}>📧</div>
            <div style={{ marginTop: 10, fontFamily: "'Space Grotesk',sans-serif", fontSize: 20, fontWeight: 700 }}>Check your inbox</div>
            <div style={{ marginTop: 8, fontSize: 14, color: "var(--text-2)", lineHeight: 1.5 }}>If an account exists for <strong style={{ color: "var(--text)" }}>{email}</strong>, a reset link is on its way.</div>
            <button className="mc-tap" onClick={() => setView("signin")} style={{ ...primaryBtn, marginTop: 18, boxShadow: "none", padding: "0 20px" }}>Back to Sign In</button>
          </div>
        )}

        {view === "verify" && (
          <div style={{ textAlign: "center", padding: "26px 20px", borderRadius: 20, background: "var(--surface)", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 40 }}>✨</div>
            <div style={{ marginTop: 10, fontFamily: "'Space Grotesk',sans-serif", fontSize: 20, fontWeight: 700 }}>Verify your email</div>
            <div style={{ marginTop: 8, fontSize: 14, color: "var(--text-2)", lineHeight: 1.5 }}>We sent a verification link to <strong style={{ color: "var(--text)" }}>{email}</strong>. Verify it, then sign in.</div>
            <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
              <button className="mc-tap" onClick={() => resendVerificationEmail().catch(() => {})} style={{ flex: 1, minHeight: 46, border: "1px solid var(--border)", borderRadius: 12, background: "transparent", color: "var(--accent-text)", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Resend link</button>
              <button className="mc-tap" onClick={() => setView("signin")} style={{ ...primaryBtn, flex: 1, minHeight: 46, boxShadow: "none" }}>Go to Sign In</button>
            </div>
          </div>
        )}

        {view === "reset" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700, textAlign: "center" }}>Reset Password</div>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" style={inputStyle} />
            </div>
            {error && <div style={{ padding: "10px 14px", borderRadius: 12, background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.35)", color: "#F87171", fontSize: 13, fontWeight: 600 }}>{error}</div>}
            <button className="mc-tap" onClick={submit} disabled={busy} style={primaryBtn}>{busy ? "Sending…" : "Send Reset Link"}</button>
            <button className="mc-tap" onClick={() => { setView("signin"); setError(null); }} style={{ minHeight: 44, border: "none", background: "transparent", color: "var(--accent-text)", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          </div>
        )}

        {(view === "signin" || view === "signup") && (
          <>
            <div style={{ display: "flex", gap: 5, padding: 5, borderRadius: 14, background: "var(--surface)", border: "1px solid var(--border)" }}>
              <button className="mc-tap" onClick={() => { setView("signin"); setError(null); }} style={tabStyle(view === "signin")}>Sign In</button>
              <button className="mc-tap" onClick={() => { setView("signup"); setError(null); }} style={tabStyle(view === "signup")}>Sign Up</button>
            </div>
            {view === "signup" && (
              <div>
                <label style={labelStyle}>Display name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Alchemist" style={inputStyle} />
              </div>
            )}
            <div>
              <label style={labelStyle}>Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input value={pwd} onChange={(e) => setPwd(e.target.value)} type="password" placeholder="Enter your password" style={inputStyle} />
            </div>
            {view === "signin" && (
              <div style={{ textAlign: "right", marginTop: -4 }}>
                <span className="mc-tap" onClick={() => { setView("reset"); setError(null); }} style={{ fontSize: 13, color: "var(--accent-text)", fontWeight: 600, cursor: "pointer" }}>Forgot password?</span>
              </div>
            )}
            {error && <div style={{ padding: "10px 14px", borderRadius: 12, background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.35)", color: "#F87171", fontSize: 13, fontWeight: 600 }}>{error}</div>}
            <button className="mc-tap" onClick={submit} disabled={busy} style={primaryBtn}>{busy ? "Please wait…" : view === "signup" ? "Create Account" : "Sign In"}</button>
            <div style={{ display: "flex", alignItems: "center", gap: 12, color: "var(--text-3)", fontSize: 12 }}>
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />or<div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            </div>
            <button className="mc-tap" onClick={google} disabled={busy} style={{ minHeight: 50, border: "1px solid var(--border)", borderRadius: 14, background: "var(--surface)", color: "var(--text)", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.9a5 5 0 0 1-2.2 3.3v2.8h3.6c2.1-2 3.2-4.9 3.2-7.9z" /><path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.6l-3.6-2.8c-1 .7-2.3 1.1-3.6 1.1-2.8 0-5.1-1.9-6-4.4H2.3v2.8A11 11 0 0 0 12 23z" /><path fill="#FBBC05" d="M6 14.3a6.6 6.6 0 0 1 0-4.2V7.3H2.3a11 11 0 0 0 0 9.9L6 14.3z" /><path fill="#EA4335" d="M12 5.5c1.6 0 3 .5 4.1 1.6l3.1-3.1A11 11 0 0 0 12 1a11 11 0 0 0-9.7 6l3.7 2.8c.9-2.5 3.2-4.3 6-4.3z" /></svg>
              Continue with Google
            </button>
          </>
        )}
      </div>
    </Screen>
  );
};

export default AuthScreen;
